import React from 'react';
import type { Ticket } from '../types/ticket';
import { StatusBadge, PriorityBadge, CategoryBadge } from './TicketBadges';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface TicketCardProps {
  ticket: Ticket;
  /** Controles opcionales (p.ej. cambio de estado en la vista admin). */
  children?: React.ReactNode;
}

const TicketCard: React.FC<TicketCardProps> = ({ ticket, children }) => (
  <div className="bg-[#1e293b] rounded-xl border border-[#334155] p-4">
    <div className="flex items-start justify-between gap-3">
      <h4 className="text-white font-semibold text-sm sm:text-base break-words">
        {ticket.subject}
      </h4>
      <div className="flex flex-wrap gap-1.5 justify-end shrink-0">
        <StatusBadge status={ticket.status} />
        <PriorityBadge priority={ticket.priority} />
      </div>
    </div>

    <div className="mt-2 flex items-center gap-2">
      <CategoryBadge category={ticket.category} />
      <span className="text-[#64748b] text-xs">
        {formatDate(ticket.createdAt)}
      </span>
    </div>

    {/*
      Quién lo pide. Solo aparece en la vista de gestión, que es donde el
      backend informa el autor; en «mis tickets» sobra, porque eres tú.
    */}
    {ticket.userEmail && (
      <p className="mt-1 text-xs text-[#94a3b8] break-words">
        {ticket.userFullName ? (
          <>
            <span className="text-[#cbd5e1]">{ticket.userFullName}</span>{' '}
            &lt;{ticket.userEmail}&gt;
          </>
        ) : (
          ticket.userEmail
        )}
      </p>
    )}

    <p className="mt-3 text-[#cbd5e1] text-sm whitespace-pre-wrap break-words">
      {ticket.description}
    </p>

    {ticket.resolutionNotes && (
      <div className="mt-3 p-3 rounded-lg bg-white/[0.03] border border-white/10">
        <p className="text-xs font-medium text-[#94a3b8] mb-1">
          Resolución
        </p>
        <p className="text-sm text-[#cbd5e1] whitespace-pre-wrap break-words">
          {ticket.resolutionNotes}
        </p>
      </div>
    )}

    {children && <div className="mt-3 pt-3 border-t border-[#334155]">{children}</div>}
  </div>
);

export default TicketCard;
